import { paloActiveFilters } from '@synergy/components-palo';

import { PaloListFilterField, PaloListFilterOperator, paloDefaultOperator } from './use-palo-list-filters';

export interface PaloFilterRow {
    key: string;
    operator: PaloListFilterOperator;
    /** One entry for most comparisons, two for a range, several for a list. */
    values: string[];
}

/** `filter[number][starts]`; the plain `filter[number]` means the field's own. */
export const paloFilterKey = (key: string, operator: PaloListFilterOperator): string => `filter[${key}][${operator}]`;

/** Comparisons that stand on their own — there is nothing to type next to them. */
export const paloOperatorTakesValue = (operator: PaloListFilterOperator): boolean =>
    'empty' !== operator && 'notEmpty' !== operator;

export const paloOperatorTakesTwo = (operator: PaloListFilterOperator): boolean => 'between' === operator;

/**
 * The rows an address already holds, in the order the list declared its fields.
 *
 * A field may stand more than once — `from` and `until` together are a range
 * someone built out of two rows, and both have to survive being read back.
 */
export const paloReadRows = (params: URLSearchParams, fields: PaloListFilterField[]): PaloFilterRow[] => {
    const rows: PaloFilterRow[] = [];

    fields.forEach(field => {
        const plain = params.get(`filter[${field.key}]`);

        if (null !== plain) {
            rows.push({ key: field.key, operator: paloDefaultOperator(field), values: plain.split(',') });
        }

        field.operators.forEach(operator => {
            const held = params.get(paloFilterKey(field.key, operator));

            if (null !== held) {
                rows.push({ key: field.key, operator, values: held.split(',') });
            }
        });
    });

    return rows;
};

/**
 * Writes the rows into the address, replacing what the catalogue's fields held.
 *
 * Only the two forms this builds are cleared. `filter[status][]`, which the
 * sub-views of the navigation send to stand for several statuses at once, has
 * a name of its own and is left exactly where it is — otherwise opening the
 * filter would empty the sub-view that led there.
 */
export const paloWriteRows = (
    params: URLSearchParams,
    fields: PaloListFilterField[],
    rows: PaloFilterRow[]
): URLSearchParams => {
    const next = new URLSearchParams(params);

    fields.forEach(field => {
        next.delete(`filter[${field.key}]`);
        field.operators.forEach(operator => next.delete(paloFilterKey(field.key, operator)));
    });

    rows.forEach(row => {
        const values = row.values.map(value => value.trim()).filter(value => '' !== value);

        if (paloOperatorTakesValue(row.operator)) {
            if (0 === values.length || (paloOperatorTakesTwo(row.operator) && values.length < 2)) {
                return;
            }

            next.set(paloFilterKey(row.key, row.operator), values.join(','));

            return;
        }

        next.set(paloFilterKey(row.key, row.operator), '1');
    });

    next.set('page', '1');

    return next;
};

/**
 * How many filters an address holds, counted the way the chips count them.
 *
 * The address is the truth: a list that never had a filter dialog has no
 * filter context around it either, and asking one would answer nothing at all.
 * The name is read out of its brackets first, so `filter[number][starts]` and
 * `filter[status][]` both count once, under the field they name.
 */
export const paloAddressFilterCount = (params: URLSearchParams): number => {
    const named = new URLSearchParams();

    Array.from(params.entries()).forEach(([key, value]) =>
        named.append(key.startsWith('filter[') ? key.substring(7, key.indexOf(']', 7)) : key, value)
    );

    return paloActiveFilters(named).length;
};

/** The address with every filter of this list taken out, the rest untouched. */
export const paloClearFilters = (params: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams();

    Array.from(params.entries())
        .filter(([key]) => !key.startsWith('filter['))
        .forEach(([key, value]) => next.append(key, value));

    next.set('page', '1');

    return next;
};
